/** @module csi/dgrid/MatrixEditor */
define([
	"dojo/_base/declare",
	"dojo/aspect",
	"dojo/on",
	"dojo/query",
	"dojo/store/Memory",
	"dijit/_Container",
	"dijit/_WidgetBase",
	"dijit/focus",
	"dgrid/ColumnSet",
	"dgrid/editor",
	"dgrid/OnDemandGrid",
	"dgrid/extensions/DijitRegistry",
	"csi/_base/underscore",
	"csi/dgrid/HeightMixin",
	"csi/dgrid/WidthMixin",
	"csi/html",
	"csi/widget/NumberTextBox"
], function(declare, aspect, on, query, Memory, _Container, _WidgetBase, focus, ColumnSet, editor, OnDemandGrid,
			DijitRegistry, _, GridHeightMixin, GridWidthMixin, html, NumberTextBox) {

	var CSS = 'csi-matrix-editor';
	var CSS_COLUMNSET = 'csi-dgrid-columnset';
	var CSS_MATRIX = 'matrix';
	var CSS_ROWS = 'rows';
	var FIELD_COLUMN_PREFIX = 'c';
	var FIELD_COLUMN_ARGS_PREFIX = 'cargs';
	var FIELD_NAME = 'name';
	var FIELD_VALUE = 'value';
	var FIELD_VALUE_ARGS = 'args';

	/**
	 * @callback MatrixEditorArgsCallback
	 * @param {Object} args
	 * @param {Object} [row] - item from rowStore; could be null if editor applies to entire matrix or column
	 * @param {Object} [column] - item from columnStore; could be null if editor applies to entire matrix or row
	 */

	/**
	 * Update all column widgets to the given newValue, except for sourceWidget whose value is already set
	 * @param {_WidgetBase[]} widgets
	 * @param {_WidgetBase} sourceWidget
	 * @param {Any} newValue
	 */
	function _changeWidgets(widgets, sourceWidget, newValue) {
		_.each(widgets, function(w) {
			if ((w != sourceWidget) && !w.get("disabled")) {
				w.set("value", newValue, false);
				w.onChange(newValue);

				// make sure text is still selected if this is the focused node
				if (w.selectAllText && (focus.curNode === w.focusNode)) {
					w.selectAllText();
				}
			}
		});
	}

	/**
	 * Return widgets in column
	 * @param {Object} column
	 * @param {_WidgetBase} widget
	 * @return {_WidgetBase[]}
	 */
	function _getColumnWidgets(column, widget) {
		return html.queryDomForWidgets(['.dgrid-column-' + column.id, _getWidgetCssQuery(widget)], column.grid.domNode);
	}

	/**
	 * Return widgets in row
	 * @param {Object} row
	 * @param {_WidgetBase} widget
	 * @return {_WidgetBase[]}
	 */
	function _getRowWidgets(row, widget) {
		return html.queryDomForWidgets([_getWidgetCssQuery(widget)], row.element);
	}

	/**
	 * Return CSS selector for widget
	 * @param {_WidgetBase} widget
	 * @return {string}
	 */
	function _getWidgetCssQuery(widget) {
		return ' .' + widget.baseClass.replace(/ /g, '.');
	}

	/**
	 * When value for entire row is changed, then update all column widgets and values of item
	 * @param {Grid} grid
	 * @param {_WidgetBase} widget
	 * @param {Any} value
	 */
	function _onChangeValue(grid, widget, value) {
		const row = grid.row(widget.domNode);
		const widgets = _getRowWidgets(row, widget);
		_changeWidgets(widgets, widget, value);
	}

	/**
	 * onFocus method for editor to scroll column set when focused
	 */
	function _onFocus() {
		var columnSet = html.getParentByClassName(this.domNode, 'dgrid-column-set');
		if (columnSet) {
			_.defer(function() {
				on.emit(columnSet, "dgrid-cellfocusin", {
					bubbles: true,
					cancelable: true
				});
			});
		}
	}

	/**
	 * Render column header with label and edit widget that will control column values.
	 * @param {DOMNode} th
	 * @param {Object} [columnItem] - columnStore item for column being rendered; could be empty if rendering column controlling all columnStore items
	 * @param {MatrixEditorArgsCallback} [callback]
	 */
	function _renderColumnHeader(column, th, columnItem, callback) {
		var grid = column.grid;
		var field = column.field;
		var store = grid.store;

		function _onChange(value) {
			// if field applies to whole matrix (top-left cell), then update all widgets in matrix; otherwise, just update widgets in column
			var widgets = (field == FIELD_VALUE) ? html.getChildWidgets(grid.domNode) : _getColumnWidgets(column, this);
			_changeWidgets(widgets, this, value);
		}

		if (column.label != null) {
			html.create('div', {
				'class': 'ellipsis',
				innerText: column.label,
				title: column.label
			}, th);
		}

		// only create widgets to apply value to entire column if more than one row
		if (store.query().length > 1) {
			const div = html.create('div', {
				'class': 'csi-matrix-editor-wrapper',
				'data-csi-automation': column.label === '' ? 'SelectAll' : 'SelectColumn-' + column.label
			}, th);

			const args = {
				onChange: _onChange
			};

			if (callback) {
				callback(args, null, columnItem);
			}

			new column.editor(args).placeAt(div);
		}
	}

	/**
	 * Render name with title - text-overflow will be handled in CSS and automation id.
	 * @param {Object} column
	 * @param {string} value
	 * @param {DOMNode} node
	 */
	function _renderNameCell(column, value, node) {
		node.setAttribute('title', value);
		node.setAttribute('data-csi-automation', value);
		html.text(node, value);
	}


	return declare([_WidgetBase, _Container], {
		/** @property {string} [columnLabel] - label for columns */
		columnLabel: '',

		/** @property {Memory} [columnStore] - store used to populate columns (query must be synchronous) */
		columnStore: null,

		/** @property {number} columnsBeforeScroll - number of matrix columns that can fit before using column set (minimum of 1) */
		columnsBeforeScroll: 6,

		/** @property {Any} defaultValue - default value for widget */
		defaultValue: 0,

		/** @property {Object} [editorArgs] - parameters to pass to editor widget */
		editorArgs: {
			constraints: {min: 0, max: 10}
		},

		/** @property {MatrixEditorArgsCallback} onEditorArgs - function called to populate arguments for editor dynamically per matrix cell */
		editorArgsCallback: null,

		/** @property {Widget} editorClass=NumberTextBox - widget class used to edit in matrix */
		editorClass: NumberTextBox,

		/** @property {string} label - label for value being edited */
		label: '',

		/** @property {string} rowLabel - label for rows */
		rowLabel: '',

		/** @property {Memory} rowStore - store used to populate rows */
		rowStore: null,

		/** @property {OnDemanGrid} _grid - grid widget */
		_grid: null,

		/**
		 * Test to see if any widgets are invalid.
		 * @return {boolean}
		 */
		isValid: function() {
			var widgets = html.getChildWidgets(this._grid.domNode);

			return _.every(widgets, function(w) {
				return html.validateWidget(w);
			});
		},

		/**
		 * Render grid
		 */
		startup: function() {
			if (this._started) {
				return;
			}
			// configure editor for grid; make sure to use string for NumberTextBox since it is still text
			var defaultValue = this.defaultValue;

			if (this.editorClass == NumberTextBox) {
				defaultValue = String(defaultValue);
			}

			var editorClass = declare(this.editorClass, _.extend({
				onFocus: _onFocus,
				value: defaultValue
			}, this.editorArgs));

			// create store for grid
			var callback = this.editorArgsCallback;
			var data = this._createData(defaultValue, callback);
			var store = new Memory({data: data});
			var grid;
			// configure columns for grid
			var matrixColumns;
			var nMatrixColumns = 0;
			if (this.columnStore) {
				const labelProperty = this.columnStore.labelProperty;
				matrixColumns = this.columnStore.query().map(function(column, i) {
					return editor({
						field: FIELD_COLUMN_PREFIX + i,
						label: column[labelProperty] || '',
						sortable: false,
						autoSave: true,
						editor: editorClass,
						canEdit: function(row, value) {
							this.editorArgs = row[FIELD_COLUMN_ARGS_PREFIX + i];
							return true;
						},
						renderHeaderCell: function(th) {
							_renderColumnHeader(this, th, column, callback);
						},
					});
				});

				nMatrixColumns = matrixColumns.length;
			}

			// configure css and non-matrix columns for grid
			var columns = [{
				field: FIELD_NAME,
				label: this.label,
				sortable: false,
				'data-automation-id': this.label,
				renderCell: _renderNameCell
			}];
			var css = [CSS];

			if (store.query().length > 1) {
				css.push(CSS_ROWS);
			}

			if (nMatrixColumns) {
				css.push(CSS_MATRIX);
			}

			if (nMatrixColumns != 1) {
				columns.push(editor({
					field: FIELD_VALUE,
					label: this.columnStore ? '' : null,
					sortable: false,
					autoSave: true,
					editor: editorClass,
					canEdit: function(row, value) {
						this.editorArgs = row[FIELD_VALUE_ARGS] || {};
						this.editorArgs.onChange = function(value) {
							_onChangeValue(grid, this, value);
						}
						return true;
					},
					renderHeaderCell: function(th) {
						_renderColumnHeader(this, th, null, callback);
					}
				}));
			}

			// render grid - if enough columns, then use column set for scrolling; otherwise just use simpler grid with columns
			const domNode = html.create('div', null, this.domNode);
			if (nMatrixColumns > Math.max(this.columnsBeforeScroll, 1)) {
				// render using column-set if horizontal scrolling is required
				css.push(CSS_COLUMNSET);
				grid = new (declare([OnDemandGrid, ColumnSet, DijitRegistry, GridHeightMixin, GridWidthMixin]))({
					className: css.join(' '),
					columnSets: [[columns], [matrixColumns]],
					minRowsPerPage: Infinity, // force dgrid to preload all rows
					store: store
				}, domNode);
			} else {
				// render using normal grid if it will fit in allocated width
				grid = new (declare([OnDemandGrid, DijitRegistry, GridHeightMixin, GridWidthMixin]))({
					className: css.join(' '),
					columns: columns.concat(matrixColumns || []),
					minRowsPerPage: Infinity, // force dgrid to preload all rows
					store: store
				}, domNode);
			}

			grid.startup();
			grid.resize();

			grid.on("dgrid-datachange", () => this.onChange(this.get("value")));

			this._grid = grid;
			this._started = true;
		},

		/**
		 * Hook that is called when a value in the matrix is changed
		 */
		onChange(value) {
		},

		/**
		 * Create store for grid with items coming from rowStore, and a property for each column in columnStore initialized to the defaultValue
		 * @param {Any} defaultValue
		 * @param {MatrixEditorArgsCallback} callback
		 * @return {Object}
		 */
		_createData: function(defaultValue, callback) {
			var rowStore = this.rowStore;
			var idProperty = rowStore.idProperty;
			var labelProperty = rowStore.labelProperty;
			return rowStore.query().map(function(row) {
				var args = {};
				var value = defaultValue;
				var item = {
					id: row[idProperty],
					name: row[labelProperty]
				};

				if (callback) {
					callback(args, row, null);
					item[FIELD_VALUE_ARGS] = args;

					if (args.value != null) {
						value = args.value;
					}
				}

				item[FIELD_VALUE] = value;

				if (this.columnStore) {
					this.columnStore.query().forEach(function(column, i) {
						var value = defaultValue;

						if (callback) {
							var args = item[FIELD_COLUMN_ARGS_PREFIX + i] = {};

							callback(args, row, column);

							if (args.value != null) {
								value = args.value;
							}
						}

						item[FIELD_COLUMN_PREFIX + i] = value;
					});
				}

				return item;
			}, this);
		},

		/**
		 * Return two-dimensional array of values from matrix
		 * @return {Any[][]}
		 */
		_getValueAttr: function() {
			var grid = this._grid;
			var widgets = html.getChildWidgets(grid.domNode);
			var columnStore = this.columnStore;
			var rowCount = this.rowStore.data.length;
			var columnCount = columnStore ? columnStore.data.length : 1;
			var rowOffset = columnCount > 1 ? 1 : 0;								// If there are more than 1 columns then there will be first column for each row.
			var startOffset = rowCount > 1 ? columnCount + rowOffset : 0;			// If there are more than 1 rows then there will a editor row for each column.

			return this.rowStore.query().map(function(row, i) {
				if (columnStore) {
					return columnStore.query().map(function(column, j) {
						var widgetIndex = startOffset * (i + 1) + rowOffset + j;
						var widgetValue = widgets[widgetIndex].get("value");
						return widgetValue;
					});
				}

				return [widgets[startOffset + i].get("value")];
			}, this);
		}
	});
});
